import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Clock, Send } from 'lucide-react';
import {
  CCACandidateQuestion,
  CCAOptionCandidate,
} from '../../utils/types/cca_candidate_types';

// ─── Types ────────────────────────────────────────────────────────────────────

type ArcherPose = 'idle' | 'draw' | 'release';

interface FlyArrow {
  offsetPath: string;
}

interface QuestionScreenProps {
  token: string;
  question: CCACandidateQuestion;
  totalAssigned: number;
  answeredCount: number;
  onAnswer: (
    selectedOptionId: string | null,
    shortAnswerText: string | null,
    timeTaken: number,
  ) => void;
}

// ─── Archer SVG ───────────────────────────────────────────────────────────────

const ArcherSVG: React.FC<{ pose: ArcherPose }> = ({ pose }) => {
  const aim = pose === 'draw';
  const shot = pose === 'release';

  return (
    <svg viewBox='0 0 110 200' width='110' height='200' className='cca-archer-svg'>
      <ellipse cx='55' cy='198' rx='26' ry='5' fill='rgba(0,0,0,0.18)' />
      <rect x='38' y='172' width='14' height='25' rx='5' fill='#2c1810' />
      <rect x='58' y='172' width='14' height='25' rx='5' fill='#2c1810' />
      <ellipse cx='45' cy='196' rx='12' ry='5' fill='#2c1810' />
      <ellipse cx='65' cy='196' rx='12' ry='5' fill='#2c1810' />
      <rect x='40' y='144' width='14' height='31' rx='6' fill='#1b4332' />
      <rect x='56' y='144' width='14' height='31' rx='6' fill='#1b4332' />
      <path d='M32 98 Q30 152 42 172 L68 172 Q80 152 78 98 Z' fill='#2d6a4f' />
      <rect x='30' y='140' width='50' height='9' rx='3' fill='#5c3317' />
      <rect x='52' y='139' width='8' height='11' rx='2' fill='#c8a84b' />
      <rect x='51' y='80' width='10' height='20' rx='5' fill='#f4d4a0' />
      <circle cx='55' cy='67' r='20' fill='#f4d4a0' />
      <ellipse cx='55' cy='50' rx='25' ry='7' fill='#1b4332' />
      <path d='M41 50 Q43 32 67 32 Q73 50 41 50Z' fill='#1b4332' />
      <path d='M67 40 C75 28 82 34 77 45 C73 36 70 37 67 40Z' fill='#c41e3a' />
      <ellipse cx='49' cy='68' rx='3.5' ry='3.5' fill='white' />
      <ellipse cx='61' cy='68' rx='3.5' ry='3.5' fill='white' />
      <circle cx={aim ? 50.5 : 49.5} cy='68' r='2.2' fill='#2c1810' />
      <circle cx={aim ? 62.5 : 61.5} cy='68' r='2.2' fill='#2c1810' />
      {aim && (
        <>
          <path d='M46 62 Q50 60 53 62' stroke='#6b4226' strokeWidth='1.5' fill='none' />
          <path d='M57 62 Q61 60 65 62' stroke='#6b4226' strokeWidth='1.5' fill='none' />
        </>
      )}
      {aim ? (
        <line x1='49' y1='77' x2='61' y2='77' stroke='#c8845a' strokeWidth='2' />
      ) : (
        <path d='M49 76 Q55 82 61 76' stroke='#c8845a' strokeWidth='1.5' fill='none' />
      )}
      <line
        x1='33'
        y1='110'
        x2={aim || shot ? 6 : 20}
        y2={aim || shot ? 104 : 112}
        stroke='#f4d4a0'
        strokeWidth='10'
        strokeLinecap='round'
      />
      <line
        x1='77'
        y1='110'
        x2={aim ? 98 : shot ? 84 : 92}
        y2={aim ? 104 : 110}
        stroke='#f4d4a0'
        strokeWidth='10'
        strokeLinecap='round'
      />
      <path
        d={aim || shot ? 'M6 88 Q-10 106 6 124' : 'M20 98 Q6 113 20 128'}
        stroke='#8B5A2B'
        strokeWidth='5'
        fill='none'
        strokeLinecap='round'
      />
      {shot ? (
        <line
          x1={aim ? 6 : 20}
          y1={aim ? 88 : 98}
          x2={aim ? 6 : 20}
          y2={aim ? 124 : 128}
          stroke='#d4c088'
          strokeWidth='1.5'
        />
      ) : (
        <>
          <line
            x1={aim ? 6 : 20}
            y1={aim ? 88 : 98}
            x2={aim ? 50 : 42}
            y2='106'
            stroke='#d4c088'
            strokeWidth='1.5'
          />
          <line
            x1={aim ? 50 : 42}
            y1='106'
            x2={aim ? 6 : 20}
            y2={aim ? 124 : 128}
            stroke='#d4c088'
            strokeWidth='1.5'
          />
        </>
      )}
      {!shot && (
        <line
          x1={aim ? 2 : 14}
          y1='106'
          x2={aim ? 50 : 44}
          y2='106'
          stroke='#c8a84b'
          strokeWidth='3'
          strokeLinecap='round'
        />
      )}
      <rect x='76' y='90' width='16' height='44' rx='6' fill='#7c4a1e' />
      <rect x='74' y='88' width='20' height='9' rx='3' fill='#5c3317' />
    </svg>
  );
};

// ─── Flying Arrow SVG ─────────────────────────────────────────────────────────

const ArrowSVG: React.FC = () => (
  <svg viewBox='0 0 90 16' width='90' height='16'>
    {/* === SHAFT (wood) === */}
    <rect x='18' y='9' width='54' height='2.5' rx='1.25' fill='#7a4e10' />
    <rect x='18' y='6.5' width='54' height='3.5' rx='1.75' fill='#c4882a' />
    <rect x='20' y='6.5' width='50' height='1.2' rx='0.6' fill='#e8c060' opacity='0.8' />
    <line x1='28' y1='7' x2='28' y2='9.5' stroke='rgba(0,0,0,0.15)' strokeWidth='0.8' />
    <line x1='40' y1='7' x2='40' y2='9.5' stroke='rgba(0,0,0,0.15)' strokeWidth='0.8' />
    <line x1='52' y1='7' x2='52' y2='9.5' stroke='rgba(0,0,0,0.15)' strokeWidth='0.8' />
    <line x1='64' y1='7' x2='64' y2='9.5' stroke='rgba(0,0,0,0.15)' strokeWidth='0.8' />
    <rect x='66' y='6.5' width='6' height='3.5' rx='0.5' fill='#5c3317' />
    <line x1='68' y1='6.5' x2='68' y2='10' stroke='rgba(0,0,0,0.3)' strokeWidth='0.5' />
    <line x1='70' y1='6.5' x2='70' y2='10' stroke='rgba(0,0,0,0.3)' strokeWidth='0.5' />
    {/* === ARROWHEAD (steel, right end, tip at x=90) === */}
    <polygon points='70,6.5 90,8 70,10.5' fill='#777' />
    <polygon points='70,5.5 90,8 70,9.5' fill='#c8c8c8' />
    <polygon points='70,5.5 86,7.8 70,7.2' fill='rgba(255,255,255,0.55)' />
    {/* === FLETCHING (feathers, left end) === */}
    <path d='M18,8 L9,1.5 L0,7.5' fill='#c41e3a' />
    <path d='M18,8 L10,2.5 L2,7.5' fill='#e84060' opacity='0.5' />
    <path d='M18,8 L9,14.5 L0,8.5' fill='#1d5c30' />
    <path d='M18,8 L10,13.5 L2,8.5' fill='#52b788' opacity='0.45' />
    <line x1='0' y1='8' x2='18' y2='8' stroke='#8b5a2b' strokeWidth='1' opacity='0.7' />
    <rect x='0' y='7' width='3' height='2' rx='0.5' fill='#2c1810' />
  </svg>
);

// ─── CCA Image ─────────────────────────────────────────────────────────────

const CCAImage: React.FC<{
  imageUrl: string | null;
  alt: string;
  className?: string;
}> = ({ imageUrl, alt, className }) => {
  if (!imageUrl) return null;
  return (
    <Box
      component='img'
      src={imageUrl}
      alt={alt}
      className={className || 'cca-q-image'}
    />
  );
};

// ─── Option row ───────────────────────────────────────────────────────────────

interface OptionProps {
  option: CCAOptionCandidate;
  letter: string;
  selected: boolean;
  disabled: boolean;
  hit: boolean;
  btnRef: (el: HTMLButtonElement | null) => void;
  onClick: () => void;
  token: string;
}

const OptionButton: React.FC<OptionProps> = ({
  option,
  letter,
  selected,
  disabled,
  hit,
  btnRef,
  onClick,
}) => (
  <button
    ref={btnRef}
    className={[
      'cca-option',
      selected ? 'cca-option--selected' : '',
      hit ? 'cca-option--hit' : '',
    ]
      .filter(Boolean)
      .join(' ')}
    onClick={onClick}
    disabled={disabled}
  >
    <span className='cca-bullseye cca-bullseye--outer' />
    <span className='cca-bullseye cca-bullseye--inner' />
    <span className='cca-option-letter'>{letter}</span>
    <span className='cca-option-content'>
      {option.text && <span className='cca-option-text'>{option.text}</span>}
      {option.imageUrl && (
        <CCAImage
          imageUrl={option.imageUrl}
          alt={`Option ${letter}`}
          className='cca-option-img'
        />
      )}
    </span>
  </button>
);

// ─── Quiver arrows ────────────────────────────────────────────────────────────

const QuiverArrow: React.FC<{ spent: boolean }> = ({ spent }) => (
  <svg
    viewBox='0 0 10 44'
    width='10'
    height='44'
    className={spent ? 'cca-arrow-spent' : 'cca-arrow-full'}
  >
    <polygon points='5,0 1,10 9,10' fill={spent ? '#555' : '#c41e3a'} />
    <rect x='4' y='9' width='2' height='28' fill={spent ? '#666' : '#c8a84b'} />
    <polygon points='2,37 8,37 5,44 3,39' fill={spent ? '#555' : '#8b6914'} />
  </svg>
);

// ─── Timer hook ───────────────────────────────────────────────────────────────

function useQuestionTimer(limitSeconds: number, onExpire: () => void) {
  const [remaining, setRemaining] = useState(limitSeconds);
  const elapsedRef = useRef(0);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    setRemaining(limitSeconds);
    elapsedRef.current = 0;
    let fired = false;

    const id = setInterval(() => {
      elapsedRef.current += 1;
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          if (!fired) {
            fired = true;
            setTimeout(() => onExpireRef.current(), 0);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [limitSeconds]);

  return {
    remaining,
    getElapsed: () => elapsedRef.current,
  };
}

// ─── Main QuestionScreen ──────────────────────────────────────────────────────

export const QuestionScreen: React.FC<QuestionScreenProps> = ({
  token,
  question,
  totalAssigned,
  answeredCount,
  onAnswer,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [shortText, setShortText] = useState('');
  const [pose, setPose] = useState<ArcherPose>('idle');
  const [flyArrow, setFlyArrow] = useState<FlyArrow | null>(null);
  const [locked, setLocked] = useState(false);
  const [hitIdx, setHitIdx] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const archerRef = useRef<HTMLDivElement>(null);
  const shortAnswerRef = useRef<HTMLTextAreaElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([
    null,
    null,
    null,
    null,
  ]);

  const isShortAnswer = question.questionType === 'short_answer';
  const pct = (rem: number) => (rem / question.timeLimitSeconds) * 100;

  const shoot = useCallback(
    (optionIdx: number | null) => {
      if (locked) return;
      setLocked(true);

      const container = containerRef.current;
      const archerEl = archerRef.current;
      let offsetPath = '';

      if (container && archerEl) {
        const cRect = container.getBoundingClientRect();
        const aRect = archerEl.getBoundingClientRect();
        const vpScale = (window as any).visualViewport?.scale ?? 1;

        const targetEl =
          optionIdx !== null
            ? optionRefs.current[optionIdx]
            : shortAnswerRef.current;

        let ex: number;
        let ey: number;
        if (targetEl) {
          const tRect = targetEl.getBoundingClientRect();
          ex = (tRect.left - cRect.left + tRect.width * 0.5) / vpScale;
          ey = (tRect.top - cRect.top + tRect.height * 0.5) / vpScale;
        } else {
          ex = cRect.width * 0.65;
          ey = cRect.height * 0.45;
        }

        let startX: number;
        let startY: number;
        if (aRect.width === 0 && aRect.height === 0) {
          startX = -20;
          startY = ey;
        } else {
          startX = (aRect.right - cRect.left - aRect.width * 0.05) / vpScale;
          startY = (aRect.top - cRect.top + aRect.height * 0.53) / vpScale;
        }

        const dx = ex - startX;
        const dy = ey - startY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const arcH = Math.min(0.12 * dist, 26);

        const ctrlX = (startX + ex) * 0.5 + (dy / dist) * arcH;
        const ctrlY = (startY + ey) * 0.5 + (-dx / dist) * arcH;

        offsetPath = `path('M ${startX.toFixed(1)} ${startY.toFixed(
          1,
        )} Q ${ctrlX.toFixed(1)} ${ctrlY.toFixed(1)} ${ex.toFixed(
          1,
        )} ${ey.toFixed(1)}')`;
      }

      setPose('draw');
      setTimeout(() => {
        setPose('release');
        if (offsetPath) setFlyArrow({ offsetPath });

        setTimeout(() => {
          setFlyArrow(null);
          setPose('idle');
        }, 750);
      }, 350);
    },
    [locked],
  );

  const handleSubmit = useCallback(
    (elapsed: number) => {
      const optionIdx = selectedOption
        ? question.options.findIndex((o) => o.id === selectedOption)
        : null;
      const validIdx =
        optionIdx !== null && optionIdx !== -1 ? optionIdx : null;
      shoot(validIdx);

      if (!isShortAnswer && validIdx !== null) {
        setTimeout(() => setHitIdx(validIdx), 900);
      }

      setTimeout(() => {
        onAnswer(
          isShortAnswer ? null : selectedOption,
          isShortAnswer ? shortText : null,
          elapsed,
        );
      }, 1050);
    },
    [
      selectedOption,
      shortText,
      isShortAnswer,
      question.options,
      shoot,
      onAnswer,
    ],
  );

  const { remaining, getElapsed } = useQuestionTimer(
    question.timeLimitSeconds,
    useCallback(() => {
      if (!locked) handleSubmit(question.timeLimitSeconds);
    }, [locked, handleSubmit, question.timeLimitSeconds]),
  );

  const timerPct = pct(remaining);
  const timerColor = timerPct > 50 ? 'green' : timerPct > 25 ? 'yellow' : 'red';

  const onClickSubmit = useCallback(() => {
    if (locked) return;
    const elapsed = Math.min(getElapsed(), question.timeLimitSeconds);
    handleSubmit(elapsed);
  }, [locked, getElapsed, question.timeLimitSeconds, handleSubmit]);

  const canSubmit = isShortAnswer
    ? shortText.trim().length > 0
    : selectedOption !== null;

  const handleOptionClick = useCallback(
    (optId: string, optIdx: number) => {
      if (locked) return;
      setSelectedOption(optId);
      shoot(optIdx);
      setTimeout(() => setHitIdx(optIdx), 900);
      setTimeout(() => {
        onAnswer(
          optId,
          null,
          Math.min(getElapsed(), question.timeLimitSeconds),
        );
      }, 1050);
    },
    [locked, shoot, onAnswer, getElapsed, question.timeLimitSeconds],
  );

  const totalDots = Math.min(totalAssigned, 30);

  return (
    <Box className='cca-stage' ref={containerRef}>
      {/* Flying arrow overlay */}
      {flyArrow && (
        <Box
          className='cca-fly-arrow'
          sx={{ offsetPath: flyArrow.offsetPath } as any}
        >
          <ArrowSVG />
        </Box>
      )}

      {/* Timer bar */}
      <Box className='cca-timer-wrap'>
        <Box
          className={`cca-timer-fill cca-timer-fill--${timerColor}`}
          sx={{ width: `${timerPct}%` }}
        />
      </Box>

      {/* Stage body */}
      <Box className='cca-stage-body'>
        {/* ── Left: Archer ── */}
        <Box className='cca-left'>
          <Box className='cca-quiver'>
            <Box className='cca-quiver-label'>Progress</Box>
            <Box className='cca-quiver-arrows'>
              {Array.from({ length: Math.min(totalAssigned, 14) }).map(
                (_, i) => (
                  <QuiverArrow key={i} spent={i < answeredCount} />
                ),
              )}
            </Box>
          </Box>
          <Box ref={archerRef} className='cca-char'>
            <ArcherSVG pose={pose} />
          </Box>
        </Box>

        {/* ── Right: Question + Options ── */}
        <Box className='cca-right'>
          {/* Question number + timer */}
          <Box className='cca-q-header'>
            <span className='cca-q-number'>
              Question {question.questionNumber} of {question.totalQuestions}
            </span>
            <Box
              component='span'
              className={`cca-q-timer-text cca-q-timer-text--${timerColor}`}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Clock size={14} /> {remaining}s
            </Box>
          </Box>

          {/* Question card */}
          <Box className='cca-question-card'>
            <Box className='cca-q-meta'>
              <span className='cca-q-meta-tag'>
                {question.subdomain.replace(/_/g, ' ')}
              </span>
              <span className='cca-q-meta-tag'>Level {question.level}</span>
              <span className='cca-q-meta-tag'>
                {isShortAnswer ? 'Short Answer' : 'Multiple Choice'}
              </span>
            </Box>
            {question.question.text && (
              <p className='cca-q-text'>{question.question.text}</p>
            )}
            {question.question.imageUrl && (
              <CCAImage
                imageUrl={question.question.imageUrl}
                alt='Question image'
              />
            )}
          </Box>

          {/* MCQ options */}
          {!isShortAnswer && (
            <Box className='cca-options-grid'>
              {question.options.map((opt, i) => (
                <OptionButton
                  key={opt.id}
                  option={opt}
                  letter={String.fromCharCode(65 + i)}
                  selected={selectedOption === opt.id}
                  disabled={locked}
                  hit={hitIdx === i}
                  btnRef={(el) => {
                    optionRefs.current[i] = el;
                  }}
                  onClick={() => handleOptionClick(opt.id, i)}
                  token={token}
                />
              ))}
            </Box>
          )}

          {/* Short answer */}
          {isShortAnswer && (
            <Box className='cca-short-answer-wrap'>
              <textarea
                ref={shortAnswerRef}
                className='cca-short-answer-input'
                placeholder='Type your answer here…'
                value={shortText}
                onChange={(e) => !locked && setShortText(e.target.value)}
                disabled={locked}
              />
            </Box>
          )}

          {/* Submit — short answer only */}
          {isShortAnswer && (
            <button
              className='cca-submit-btn'
              onClick={onClickSubmit}
              disabled={locked || !canSubmit}
            >
              <Send size={15} style={{ display: 'inline', marginRight: 6 }} />
              Submit Answer
            </button>
          )}

          {/* Progress dots */}
          <Box className='cca-progress'>
            {Array.from({ length: totalDots }).map((_, i) => {
              const cls =
                i < answeredCount
                  ? 'cca-dot--done'
                  : i === answeredCount
                    ? 'cca-dot--current'
                    : 'cca-dot--empty';
              return (
                <Box component='span' key={i} className={`cca-dot ${cls}`} />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionScreen;
